/* =========================================================================
   BIGOLI — shape line-up motion controller.

   Pairs with assets/bigoli-cast-motion.css and sections/bigoli-cast.liquid.
   The motion itself is all CSS; this file only decides WHEN it may run and
   WHICH character is the chosen one.

   Three jobs, nothing else:

   1. COST. Six looping animations that keep running behind a scrolled page
      or a backgrounded tab are a battery bill for nothing. An
      IntersectionObserver pauses the whole row when it leaves the viewport
      and the Page Visibility API pauses it when the tab is hidden.
      will-change is only set while the row is actually on screen, and taken
      straight back off, so the compositor is not asked to hold six layers
      for a row nobody is looking at.

   2. CHOICE. Tapping or clicking a character already selects that shape in
      the picker further down the page (sections/bigoli-picker.liquid owns
      that; it listens for [data-bgl-shape-link]). This file adds the
      feedback: the chosen character plays its one-shot celebration once,
      then settles into a quiet resting state that stays. Because the real
      state lives in the picker's radios, it also listens to those, so
      choosing a shape down in the picker lights up the same character in
      the line-up.

   3. REDUCED MOTION. If the visitor has asked for reduced motion the
      celebration is never applied and no observer is attached; the CSS has
      already stopped every loop. The preference is re-read live, so
      changing it mid-visit takes effect without a reload.

   No layout is read or written here, so nothing in this file can shift the
   page.
   ========================================================================= */
(function () {
  "use strict";

  var reduceQuery = window.matchMedia
    ? window.matchMedia('(prefers-reduced-motion: reduce)')
    : null;

  function prefersReduced() {
    return !!(reduceQuery && reduceQuery.matches);
  }

  function closestFig(node) {
    while (node && node.nodeType === 1) {
      if (node.hasAttribute && node.hasAttribute('data-bgl-char')) { return node; }
      node = node.parentNode;
    }
    return null;
  }

  function setUp(row) {
    if (row.getAttribute('data-bgl-cast-ready') === '1') { return; }
    row.setAttribute('data-bgl-cast-ready', '1');

    var figs = Array.prototype.slice.call(row.querySelectorAll('[data-bgl-char]'));
    if (!figs.length) { return; }

    var celebrationTimer = null;
    var observer = null;

    /* ---------------- 1. cost ---------------- */

    function live(on) {
      row.classList.toggle('is-paused', !on);
      row.classList.toggle('is-live', !!on);
    }

    function startObserving() {
      if (observer || prefersReduced() || !('IntersectionObserver' in window)) { return; }
      observer = new IntersectionObserver(function (entries) {
        for (var i = 0; i < entries.length; i++) {
          live(entries[i].isIntersecting && !document.hidden);
        }
      }, { rootMargin: '120px 0px' });
      observer.observe(row);
    }

    function stopObserving() {
      if (observer) { observer.disconnect(); observer = null; }
      row.classList.remove('is-paused');
      row.classList.remove('is-live');
    }

    document.addEventListener('visibilitychange', function () {
      if (!observer) { return; }
      if (document.hidden) {
        live(false);
      } else {
        /* Re-arm so the observer re-reports the row's current position. */
        observer.unobserve(row);
        observer.observe(row);
      }
    });

    /* ---------------- 2. choice ---------------- */

    function clearCelebration() {
      if (celebrationTimer) { window.clearTimeout(celebrationTimer); celebrationTimer = null; }
      for (var i = 0; i < figs.length; i++) { figs[i].classList.remove('is-celebrating'); }
    }

    function pick(name, celebrate) {
      var wanted = String(name || '').toLowerCase();
      var found = false;

      clearCelebration();

      for (var i = 0; i < figs.length; i++) {
        var fig = figs[i];
        var isIt = fig.getAttribute('data-bgl-char') === wanted;

        fig.classList.toggle('is-picked', isIt);

        if (isIt) {
          fig.setAttribute('aria-current', 'true');
          found = true;
          if (celebrate && !prefersReduced()) {
            /* Force a restart when the same character is chosen twice in a
               row: drop the class, flush it, put it back. */
            fig.classList.remove('is-celebrating');
            void fig.offsetWidth;
            fig.classList.add('is-celebrating');
          }
        } else {
          fig.removeAttribute('aria-current');
        }
      }

      if (found && celebrate) {
        /* animationend is the normal exit. This is the safety net for the
           case where the row is paused off screen and the one-shot never
           reaches its end — nobody is watching it, so dropping it is right. */
        celebrationTimer = window.setTimeout(clearCelebration, 1600);
      }
    }

    row.addEventListener('animationend', function (e) {
      var fig = closestFig(e.target);
      if (fig) { fig.classList.remove('is-celebrating'); }
    });

    row.addEventListener('click', function (e) {
      var fig = closestFig(e.target);
      if (!fig) { return; }
      /* The href still carries the scroll to the picker and the picker still
         does the selecting. This only paints the line-up. */
      pick(fig.getAttribute('data-bgl-char'), true);
    });

    /* The picker's shape radios are the real state. Mirror them, so choosing
       a shape down there lights the same character up here. Read only —
       nothing in the picker is written to from this file. */
    document.addEventListener('change', function (e) {
      var input = e.target;
      if (!input || !input.hasAttribute || !input.hasAttribute('data-bgl-shape')) { return; }
      if (!input.checked) { return; }
      pick(input.getAttribute('data-name'), true);
    });

    /* Nothing is marked as chosen on load. The picker has a default shape,
       but the visitor has not chosen anything yet, and a character sitting
       in the "you picked me" state before anyone touched it is a lie. */

    /* ---------------- 3. reduced motion ---------------- */

    function applyPreference() {
      if (prefersReduced()) {
        clearCelebration();
        stopObserving();
      } else {
        startObserving();
      }
    }

    if (reduceQuery) {
      if (reduceQuery.addEventListener) { reduceQuery.addEventListener('change', applyPreference); }
      else if (reduceQuery.addListener) { reduceQuery.addListener(applyPreference); }
    }

    applyPreference();
  }

  function init() {
    var rows = document.querySelectorAll('[data-bgl-cast]');
    Array.prototype.forEach.call(rows, setUp);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* Theme editor: sections are re-rendered without a page load. */
  document.addEventListener('shopify:section:load', init);
})();
